import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const FeeRegister = () => {
  const storedSession = localStorage.getItem("currentSessionId") || "";
  const [currentSession] = useState(storedSession);

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const sessionMonths = [
    "April","May","June","July","August","September",
    "October","November","December","January","February","March"
  ];

  // ** Set expected fees here — adjust as needed **
  const expectedMonthlyFee = 1500; // example expected monthly fee
  const expectedBusFee = 1000;     // example expected bus fee per month

  /* ================= FETCH DATA ================= */

  useEffect(() => {
    if (!currentSession) return;
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses`,
      { params: { sessionId: currentSession } }
    ).then(res => setClasses(res.data))
     .catch(console.error);
  }, [currentSession]);

  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }

    axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName`,
      { params: { sessionId: currentSession } }
    ).then(res => setSections(res.data || []))
     .catch(console.error);
  }, [selectedClass, currentSession]);

  useEffect(() => {
    if (!selectedClass || !selectedSection) {
      setStudents([]);
      return;
    }

    axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/fees/report/monthly-fee-status-ledgers`,
      {
        params: {
          sessionId: currentSession,
          className: selectedClass,
          sectionName: selectedSection
        }
      }
    ).then(res => setStudents(res.data || []))
     .catch(console.error);
  }, [selectedClass, selectedSection, currentSession]);

  /* ================= PRINT (UNCHANGED) ================= */

  const handlePrint = () => {
    if (!students.length) return;

    const printWindow = window.open("", "_blank");

    let tableHTML = `
      <table>
        <thead>
          <tr>
            <th rowspan="2">Sr</th>
            <th rowspan="2">Student Name</th>
    `;

    sessionMonths.forEach(month => {
      tableHTML += `<th colspan="2">${month}</th>`;
    });

    tableHTML += `</tr><tr>`;

    sessionMonths.forEach(() => {
      tableHTML += `<th>Monthly</th><th>Bus</th>`;
    });

    tableHTML += `</tr></thead><tbody>`;

    students.forEach((s, i) => {
      tableHTML += `<tr>
        <td>${i + 1}</td>
        <td>${s.name}</td>`;

      sessionMonths.forEach((_, midx) => {
        const monthly = s.monthlyStatus[midx]?.value || "";
        const bus = s.busStatus ? s.busStatus[midx]?.value || "N/A" : "N/A";

        tableHTML += `<td>${monthly}</td><td>${bus}</td>`;
      });

      tableHTML += `</tr>`;
    });

    tableHTML += `</tbody></table>`;

    printWindow.document.write(`
      <html>
      <head>
        <title>Fee Register</title>
        <style>
          @page { size: A4 landscape; margin: 10mm; }
          body { font-family: Arial; }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8px;
          }
          th, td {
            border: 1px solid black;
            padding: 3px;
            text-align: center;
          }
          th { background: #eee; }
        </style>
      </head>
      <body>
        <h3 style="text-align:center">
          Fee Register - ${selectedClass} ${selectedSection}
        </h3>
        ${tableHTML}
        <p style="text-align:right; margin-top:10px;">
          Total Students: ${students.length}
        </p>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  /* ================= EXPORT (UPDATED with pending calc) ================= */

  const handleExport = () => {
    if (!students.length) return;

    const wb = XLSX.utils.book_new();

    // Prepare data array for worksheet
    let data = [];

    // Header row 1 (Months + totals)
    const header1 = ["Sr No", "Student Name"];
    sessionMonths.forEach(month => {
      header1.push(month, ""); // two columns: Monthly & Bus
    });
    header1.push("Total Received", "Total Pending"); // totals columns
    data.push(header1);

    // Header row 2 (Monthly & Bus)
    const header2 = ["", ""];
    sessionMonths.forEach(() => {
      header2.push("Monthly", "Bus");
    });
    header2.push("", "");
    data.push(header2);

    // Student rows
    students.forEach((s, idx) => {
      let row = [idx + 1, s.name];
      let totalReceived = 0;
      let totalPending = 0;

      sessionMonths.forEach((_, midx) => {
        const monthly = Number(s.monthlyStatus[midx]?.value) || 0;
        const bus = Number(s.busStatus?.[midx]?.value) || 0;

        row.push(monthly, bus);

        totalReceived += monthly + bus;

        const pendingMonthly = Math.max(0, expectedMonthlyFee - monthly);
        const pendingBus = Math.max(0, expectedBusFee - bus);

        totalPending += pendingMonthly + pendingBus;
      });

      row.push(totalReceived, totalPending);

      data.push(row);
    });

    // Totals row
    const totalRow = ["", "Total Students: " + students.length];
    sessionMonths.forEach(() => {
      totalRow.push("", "");
    });

    // Grand totals
    const grandTotalReceived = students.reduce((sum, s) => {
      let studentSum = 0;
      sessionMonths.forEach((_, midx) => {
        studentSum += Number(s.monthlyStatus[midx]?.value) || 0;
        studentSum += Number(s.busStatus?.[midx]?.value) || 0;
      });
      return sum + studentSum;
    }, 0);

    const grandTotalPending = students.reduce((sum, s) => {
      let studentPending = 0;
      sessionMonths.forEach((_, midx) => {
        const monthly = Number(s.monthlyStatus[midx]?.value) || 0;
        const bus = Number(s.busStatus?.[midx]?.value) || 0;
        studentPending += Math.max(0, expectedMonthlyFee - monthly);
        studentPending += Math.max(0, expectedBusFee - bus);
      });
      return sum + studentPending;
    }, 0);

    totalRow.push(grandTotalReceived, grandTotalPending);
    data.push(totalRow);

    // Convert to worksheet
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Merge Month headers (first row)
    let colStart = 2;
    ws["!merges"] = ws["!merges"] || [];
    sessionMonths.forEach(() => {
      ws["!merges"].push({ s: { r: 0, c: colStart }, e: { r: 0, c: colStart + 1 } });
      colStart += 2;
    });

    // Merge total headers cells
    ws["!merges"].push({ s: { r: 0, c: colStart }, e: { r: 1, c: colStart } }); // Total Received
    ws["!merges"].push({ s: { r: 0, c: colStart + 1 }, e: { r: 1, c: colStart + 1 } }); // Total Pending

    // Set column widths
    ws["!cols"] = [{ wch: 8 }, { wch: 25 }];
    sessionMonths.forEach(() => {
      ws["!cols"].push({ wch: 10 }, { wch: 10 });
    });
    ws["!cols"].push({ wch: 15 }, { wch: 15 });

    // Apply basic styles (optional, can remove if no style support)
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = 0; R <= range.e.r; ++R) {
      for (let C = 0; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellAddress];
        if (!cell) continue;
        cell.s = {
          font: { name: "Arial", bold: R < 2 || R === range.e.r },
          alignment: { horizontal: "center", vertical: "center" },
          fill:
            R === 0
              ? { fgColor: { rgb: "4F81BD" } }
              : R === 1
              ? { fgColor: { rgb: "D9E1F2" } }
              : R === range.e.r
              ? { fgColor: { rgb: "FFFFCC" } }
              : undefined,
        };
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, "Fee Register");
    XLSX.writeFile(wb, `Fee_Register_${selectedClass}_${selectedSection}.xlsx`);
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-xl p-6">

        <h2 className="text-2xl font-bold mb-6 text-gray-700">
          Fee Register
        </h2>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <select
            value={selectedClass}
            onChange={e => setSelectedClass(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="">Select Class</option>
            {classes.map(c => (
              <option key={c._id} value={c.className}>{c.className}</option>
            ))}
          </select>

          <select
            value={selectedSection}
            onChange={e => setSelectedSection(e.target.value)}
            disabled={!selectedClass}
            className="border rounded px-3 py-2"
          >
            <option value="">Select Section</option>
            {sections.map(s => (
              <option key={s._id} value={s.sectionName}>{s.sectionName}</option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Print
            </button>
            <button
              onClick={handleExport}
              className="bg-green-600 text-white px-4 py-2 rounded"
            >
              Export
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border text-center">
            <thead className="bg-gray-200">
              <tr>
                <th rowSpan={2} className="border px-2 py-2">Sr</th>
                <th rowSpan={2} className="border px-2 py-2">Student Name</th>
                {sessionMonths.map((month, idx) => (
                  <th key={idx} colSpan={2} className="border px-2 py-2">
                    {month}
                  </th>
                ))}
              </tr>
              <tr>
                {sessionMonths.map((_, idx) => (
                  <React.Fragment key={idx}>
                    <th className="border px-2 py-1 text-sm bg-gray-100">Monthly</th>
                    <th className="border px-2 py-1 text-sm bg-gray-100">Bus</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>

            <tbody>
              {students.map((s, idx) => (
                <tr key={idx}>
                  <td className="border px-2 py-2">{idx + 1}</td>
                  <td className="border px-2 py-2">{s.name}</td>
                  {sessionMonths.map((_, midx) => (
                    <React.Fragment key={midx}>
                      <td className="border px-2 py-2">
                        {s.monthlyStatus[midx]?.value || 0}
                      </td>
                      <td className="border px-2 py-2">
                        {s.busStatus ? s.busStatus[midx]?.value || 0 : 0}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default FeeRegister;